
import { useCallback } from 'react';
import { WordRecyclingProps } from './recycling/types';

/**
 * Hook for handling word recycling functionality
 */
export const useWordRecycling = ({
  wordsToReview,
  setWordsToReview,
  setIsPerfectLevel,
  setShowLevelComplete,
  currentLevelId
}: WordRecyclingProps) => {
  
  /**
   * Resets recycling counts for a new lesson or level
   */
  const resetRecyclingCounts = useCallback(() => {
    try {
      // Clear any recycled word tracking 
      const keys = Object.keys(localStorage);
      const recycledWordKeys = keys.filter(key => key.startsWith('recycled_word_'));
      
      if (recycledWordKeys.length > 0) {
        console.log(`🧹 [resetRecyclingCounts] Cleaning up ${recycledWordKeys.length} recycled word counters`);
        recycledWordKeys.forEach(key => {
          localStorage.removeItem(key);
        });
      }
      
      // Clear level-specific recycling flags
      if (currentLevelId) {
        localStorage.removeItem(`level_${currentLevelId}_had_recycled_words`);
      }
      
      // Reset global recycling flag
      (window as any).__HAD_RECYCLED_WORDS__ = false;
      
    } catch (e) {
      console.error('Error resetting recycling counts:', e);
    }
  }, [currentLevelId]);
  
  /**
   * Handles failed words without adding them back to exercises
   * This maintains compatibility with the activity logging system
   */
  const recycleFailedWords = useCallback((failedWords: string[]) => {
    if (!failedWords || failedWords.length === 0) return;
    
    console.log(`⚠️ [recycleFailedWords] Processing ${failedWords.length} failed words`);
    
    // Mark the level as imperfect when there are failed words
    setIsPerfectLevel(false);
    
    // Update global flag to track failed words
    (window as any).__HAD_RECYCLED_WORDS__ = true;
    (window as any).__PERFECT_LEVEL_STATUS__ = false;
    
    // Store in localStorage so perfect tracking persists
    try {
      if (currentLevelId) {
        localStorage.setItem(`level_${currentLevelId}_had_recycled_words`, 'true');
      }
    } catch (e) {
      console.error('Error saving recycling status to localStorage:', e);
    }
    
    // Proceed with level completion even though there were failed words
    setShowLevelComplete(true);
  }, [currentLevelId, setIsPerfectLevel, setShowLevelComplete]);
  
  return {
    recycleFailedWords,
    resetRecyclingCounts
  };
};
